import React, { useState } from 'react';
import { Item } from '../interfaces';
import { websites } from '../websites';
import Portal from './Portal';

interface SettingsPopupProps {
  item: Item;
  onClose: () => void;
  onSave: (item: Item, settings: { is_tracked_periodically: boolean; selected_websites: string[]; use_ai_to_find_product: boolean }) => void;
}

const SettingsPopup: React.FC<SettingsPopupProps> = ({ item, onClose, onSave }) => {
  const [isTracked, setIsTracked] = useState(item.is_tracked_periodically ?? false);
  const [selectedWebsites, setSelectedWebsites] = useState<string[]>(item.selected_websites ?? []);
  const [useAIToFindProduct, setUseAIToFindProduct] = useState(item.use_ai_to_find_product ?? true);

  const handleSave = () => {
    onSave(item, {
      is_tracked_periodically: isTracked,
      selected_websites: selectedWebsites,
      use_ai_to_find_product: useAIToFindProduct,
    });
    onClose();
  };

  const handleWebsiteChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const options = Array.from(e.target.options);
    const selected = options.filter(option => option.selected).map(option => option.value);
    setSelectedWebsites(selected);
  };

  return (
    <Portal>
      <div className="modal show d-block" style={{ backgroundColor: 'rgba(15, 23, 42, 0.6)', backdropFilter: 'blur(4px)' }} tabIndex={-1}>
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content border-0 shadow-lg rounded-4 overflow-hidden">
            <div className="modal-header border-bottom-0 pt-4 px-4 d-flex align-items-center justify-content-between">
              <h5 className="modal-title fw-bold">Settings: {item.item_name}</h5>
              <button type="button" className="btn-close shadow-none" onClick={onClose} aria-label="Close"></button>
            </div>
            <div className="modal-body px-4 pb-4">
              <div className="bg-light bg-opacity-50 p-3 rounded-3 mb-4 border">
                <div className="form-check form-switch mb-0 d-flex align-items-center justify-content-between p-0">
                  <label className="form-check-label fw-semibold mb-0" htmlFor="use-ai-switch">
                    Use Smart AI Search
                  </label>
                  <input
                    className="form-check-input ms-0 mt-0"
                    type="checkbox"
                    role="switch"
                    id="use-ai-switch"
                    checked={useAIToFindProduct}
                    onChange={(e) => setUseAIToFindProduct(e.target.checked)}
                    style={{ width: '2.5em', height: '1.25em' }}
                  />
                </div>
                <p className="small text-muted mb-0 mt-2">
                  Automatically find and track the best deals across multiple stores.
                </p>
              </div>

              <div className={`mb-4 transition-opacity ${!useAIToFindProduct ? 'opacity-50 pointer-events-none' : ''}`}>
                <div className="form-check form-switch d-flex align-items-center justify-content-between p-0 mb-3">
                  <label className="form-check-label fw-semibold mb-0" htmlFor="periodic-tracking-switch">
                    Enable Periodic Tracking
                  </label>
                  <input
                    className="form-check-input ms-0 mt-0"
                    type="checkbox"
                    role="switch"
                    id="periodic-tracking-switch"
                    checked={isTracked}
                    onChange={(e) => setIsTracked(e.target.checked)}
                    disabled={!useAIToFindProduct}
                    style={{ width: '2.5em', height: '1.25em' }}
                  />
                </div>
                <div className="bg-info bg-opacity-10 text-info p-2 rounded small border-info border-opacity-10 mb-3">
                  <i className="bi bi-clock-history me-2"></i>
                  Updates approximately every 24 hours.
                </div>
              </div>

              <div className={`mb-2 transition-opacity ${!useAIToFindProduct ? 'opacity-50 pointer-events-none' : ''}`}>
                <label htmlFor="website-select" className="form-label fw-semibold small text-uppercase text-muted">
                  Preferred Stores
                </label>
                <select
                  multiple
                  className="form-select border-2"
                  id="website-select"
                  value={selectedWebsites}
                  onChange={handleWebsiteChange}
                  disabled={!useAIToFindProduct}
                  style={{ minHeight: '120px' }}
                >
                  {websites.map((website) => (
                    <option key={website} value={website} className="py-1">
                      {website}
                    </option>
                  ))}
                </select>
                <div className="form-text small mt-2">
                  <i className="bi bi-info-circle me-1"></i>
                  Hold Ctrl/Cmd to select multiple, or leave empty for auto-discovery.
                </div>
              </div>
            </div>
            <div className="modal-footer border-top-0 px-4 pb-4 pt-0">
              <button type="button" className="btn btn-light px-4" onClick={onClose}>
                Cancel
              </button>
              <button type="button" className="btn btn-primary px-4 fw-bold" onClick={handleSave}>
                Apply Changes
              </button>
            </div>
          </div>
        </div>
      </div>
    </Portal>
  );
};

export default SettingsPopup;